import { useEffect, useMemo, useRef, useState } from "react"
import { motion } from "motion/react"
import { useRequest } from "../../hooks/useRequest"
import { appStore } from "../../store/appStore"
import { userStore } from "../../store/userStore"
import { PaymentMethod, SbpPaymentResponse } from "../../types/steps/buy"
import { Ticket } from "../../types/entities/ticket"
import Loader from "../../components/Loader"
import Input from "../../components/Input"
import ConfirmModal, { ConfirmModalHandle } from "../../components/ConfirmModal"
import { Sbp as SbpIcon } from "../../components/svgCollection"
import { extractOrderHoldDates } from "../../lib/paymentHold"
import CardPaymentFrame from "../buy/CardPaymentFrame"
import PaymentCheckingPopup from "../buy/PaymentCheckingPopup"
import {
    clearOrderPaymentMethod,
    getOrderPaymentMethod,
    ORDER_PAYMENT_METHOD_LABELS,
    OrderPaymentMethodSlug,
    resolveOrderPaymentMethod,
    saveOrderPaymentMethod,
} from "../../lib/orderPaymentMethodStorage"
import SbpOrderPayment from "../buy/SbpOrderPayment"

const BODY = "'Manrope', sans-serif"

type Props = {
    ticket: Ticket
    onUpdated: () => void
}

function toUiMethod(slug: OrderPaymentMethodSlug): PaymentMethod {
    return {
        type: slug === "pushkin_card" ? "pushkin" : slug,
        label: ORDER_PAYMENT_METHOD_LABELS[slug],
    }
}

export default function UnpaidOrderPayment({ ticket, onUpdated }: Props) {
    const { request, requestData } = useRequest()
    const { cinema } = appStore()
    const { user } = userStore()
    const confirmRef = useRef<ConfirmModalHandle>(null)
    const pollRef = useRef<number | null>(null)
    const lockedMethod = useMemo(() => resolveOrderPaymentMethod(ticket), [ticket])
    const [methods, setMethods] = useState<PaymentMethod[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingMethods, setLoadingMethods] = useState(true)
    const [sbpOpen, setSbpOpen] = useState(false)
    const [cardPaymentUrl, setCardPaymentUrl] = useState<string | null>(null)
    const [checkingPayment, setCheckingPayment] = useState(false)
    const [pushkinForm, setPushkinForm] = useState({
        lastname: user?.last_name || "",
        firstname: user?.first_name || "",
        patronymic: user?.patronymic || "",
    })
    const [sbpParams, setSbpParams] = useState<{
        orderId: string
        orderNumber: string
        qrImage?: string
        sbpBanks?: SbpPaymentResponse["sbp_banks"]
        orderCreatedAt?: string
        orderExpiredAt?: string
    } | null>(null)

    useEffect(() => {
        const fromApi = ticket.payment_info?.method_slug
        if (fromApi && !getOrderPaymentMethod(ticket.id)) {
            saveOrderPaymentMethod(ticket.id, fromApi)
        }
    }, [ticket.id, ticket.payment_info?.method_slug])

    useEffect(() => {
        const load = async () => {
            setLoadingMethods(true)
            try {
                if (lockedMethod) {
                    setMethods([toUiMethod(lockedMethod)])
                    return
                }

                const base: PaymentMethod[] = [{ type: "card", label: "Банковская карта" }]
                const [sbp, sberpay] = await Promise.all([
                    request("GET", `cinema/${cinema.id}/sbp`),
                    request("GET", `cinema/${cinema.id}/sberpay`),
                ])
                if (sbp) base.unshift({ type: "sbp", label: "СБП" })
                if (sberpay) base.unshift({ type: "sberpay", label: "SberPay" })
                setMethods(base)
            } finally {
                setLoadingMethods(false)
            }
        }
        load()
    }, [cinema.id, lockedMethod])

    const orderDates = extractOrderHoldDates({ created_at: ticket.created_at, expired_at: ticket.expired_at })

    const stopPolling = () => {
        if (pollRef.current != null) {
            window.clearInterval(pollRef.current)
            pollRef.current = null
        }
    }

    const finishPaymentFlow = (orderId: string) => {
        stopPolling()
        setCardPaymentUrl(null)
        setCheckingPayment(false)
        clearOrderPaymentMethod(orderId)
        onUpdated()
    }

    const pollUntilPaid = (orderId: string, fast = false) => {
        stopPolling()
        pollRef.current = window.setInterval(async () => {
            try {
                const order = await request<{ status: { id: number } }>(
                    "GET",
                    `user/order/${orderId}`,
                    undefined,
                    { disableError: true },
                )
                if (order.status.id === 3 || order.status.id === 5) {
                    finishPaymentFlow(orderId)
                }
            } catch {
                /* ignore */
            }
        }, fast ? 2000 : 5000)
        window.setTimeout(() => stopPolling(), 15 * 60 * 1000)
    }

    useEffect(() => () => stopPolling(), [])

    const startPayment = async (apiMethod: OrderPaymentMethodSlug, extra?: { fio: string }) => {
        setLoading(true)
        try {
            const resPayment = await requestData<SbpPaymentResponse & { order_number: string; qrImage?: string }>({
                type: "POST",
                path: `user/order/${ticket.id}/payment`,
                sendData: { method: apiMethod, ...extra },
                disableError: true,
            })

            if (resPayment.status === "error") {
                appStore.getState().setError(resPayment.error.message)
                return
            }

            saveOrderPaymentMethod(ticket.id, apiMethod)

            if (apiMethod === "sbp") {
                setSbpParams({
                    orderId: ticket.id,
                    orderNumber: resPayment.data.order_number,
                    qrImage: resPayment.data.qrImage,
                    sbpBanks: resPayment.data.sbp_banks,
                    ...orderDates,
                })
                setSbpOpen(true)
                return
            }

            setCardPaymentUrl(resPayment.data.redirect_url ?? "")
            pollUntilPaid(ticket.id, true)
        } finally {
            setLoading(false)
        }
    }

    const pay = async (method: PaymentMethod["type"]) => {
        if (method === "pushkin") return
        await startPayment(method as OrderPaymentMethodSlug)
    }

    const payPushkin = async () => {
        if (!pushkinForm.lastname.trim() || !pushkinForm.firstname.trim()) {
            appStore.getState().setError("Укажите фамилию и имя")
            return
        }
        await startPayment("pushkin_card", {
            fio: `${pushkinForm.lastname} ${pushkinForm.firstname} ${pushkinForm.patronymic}`.trim(),
        })
    }

    if (loadingMethods) {
        return (
            <div className="flex justify-center py-2">
                <Loader />
            </div>
        )
    }

    return (
        <>
            <ConfirmModal ref={confirmRef} />
            {sbpOpen && sbpParams && (
                <SbpOrderPayment
                    {...sbpParams}
                    initialSbpBanks={sbpParams.sbpBanks}
                    asModal
                    onClose={() => setSbpOpen(false)}
                    onPaid={() => {
                        setSbpOpen(false)
                        clearOrderPaymentMethod(ticket.id)
                        onUpdated()
                    }}
                    onExpired={() => {
                        setSbpOpen(false)
                        clearOrderPaymentMethod(ticket.id)
                        onUpdated()
                    }}
                />
            )}
            {cardPaymentUrl && !checkingPayment && (
                <CardPaymentFrame
                    url={cardPaymentUrl}
                    orderId={ticket.id}
                    onReturnDetected={() => {
                        setCheckingPayment(true)
                        setCardPaymentUrl(null)
                        pollUntilPaid(ticket.id, true)
                    }}
                    onClose={() => setCardPaymentUrl(null)}
                    onFrameNavigated={() => {
                        void (async () => {
                            try {
                                const order = await request<{ status: { id: number } }>(
                                    "GET",
                                    `user/order/${ticket.id}`,
                                    undefined,
                                    { disableError: true },
                                )
                                if (order.status.id === 3 || order.status.id === 5) {
                                    finishPaymentFlow(ticket.id)
                                }
                            } catch {
                                /* ignore */
                            }
                        })()
                    }}
                />
            )}
            {checkingPayment && <PaymentCheckingPopup />}
            {lockedMethod === "pushkin_card" ? (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-center gap-2 text-[14px] text-white" style={{ fontFamily: BODY, fontWeight: 700 }}>
                        <img src={require("../../assets/pushkin.png")} alt="" className="h-6" />
                        Пушкинская карта
                    </div>
                    <div className="flex w-full flex-col gap-2.5">
                        <Input
                            value={pushkinForm.lastname}
                            required
                            label="Фамилия"
                            placeholder="Введите фамилию"
                            onChange={value => setPushkinForm({ ...pushkinForm, lastname: value })}
                        />
                        <Input
                            value={pushkinForm.firstname}
                            required
                            label="Имя"
                            placeholder="Введите имя"
                            onChange={value => setPushkinForm({ ...pushkinForm, firstname: value })}
                        />
                        <Input
                            value={pushkinForm.patronymic}
                            label="Отчество"
                            placeholder="Введите отчество"
                            onChange={value => setPushkinForm({ ...pushkinForm, patronymic: value })}
                        />
                    </div>
                    <motion.button
                        type="button"
                        disabled={loading}
                        whileTap={{ scale: 0.98 }}
                        onClick={payPushkin}
                        className={`relative flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-white/15 px-8 text-[14px] text-white transition-all hover:opacity-90 ${loading ? "opacity-50" : ""}`}
                        style={{ fontFamily: BODY, fontWeight: 700 }}>
                        Оплатить
                    </motion.button>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {methods.map(item => (
                        <motion.button
                            key={item.type}
                            type="button"
                            disabled={loading}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => pay(item.type)}
                            className={`relative flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-white/15 px-8 text-[14px] text-white transition-all hover:opacity-90 ${loading ? "opacity-50" : ""}`}
                            style={{
                                ...(item.type === "sbp" ? { backgroundColor: "#F5F1E8", color: "#1a2d55" } : {}),
                                fontFamily: BODY,
                                fontWeight: 700,
                            }}>
                            {item.type === "sbp" ? <SbpIcon /> : item.label}
                        </motion.button>
                    ))}
                </div>
            )}
        </>
    )
}
